import {
  Badge,
  BlockStack,
  Box,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Text,
} from '@shopify/polaris';
import type { AbGroup } from '@/queries/notifications';

export interface ABResultsInlineProps {
  groups: AbGroup[];
  limit?: number;
}

export function ABResultsInline({ groups, limit = 3 }: ABResultsInlineProps) {
  if (!groups.length) return null;
  const recent = groups.slice(0, limit);

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <Text as="h3" variant="headingSm">A/B Tests</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              آخر {recent.length} نتائج
            </Text>
          </BlockStack>
          {groups.length > limit ? (
            <Badge tone="info">{`+${groups.length - limit} أكتر`}</Badge>
          ) : null}
        </InlineStack>

        <BlockStack gap="300">
          {recent.map((g) => (
            <ABGroupRow key={g.id} group={g} />
          ))}
        </BlockStack>
      </BlockStack>
    </Card>
  );
}

function ABGroupRow({ group: g }: { group: AbGroup }) {
  const a = g.variants.A;
  const b = g.variants.B;
  const aRate = a && a.sent ? a.opened / a.sent : 0;
  const bRate = b && b.sent ? b.opened / b.sent : 0;
  const winner = a && b ? (aRate > bRate ? 'A' : bRate > aRate ? 'B' : null) : null;
  const fmt = (e: number) => {
    try { return new Date(e * 1000).toLocaleDateString(); } catch { return '—'; }
  };

  return (
    <Box background="bg-surface-secondary" padding="300" borderRadius="200">
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="p" variant="bodySm" tone="subdued">
            {fmt(g.created_at)} · {g.id.slice(0, 8)}…
          </Text>
          {winner ? (
            <Badge tone="success">{`Winner: Variant ${winner}`}</Badge>
          ) : (
            <Badge>تعادل</Badge>
          )}
        </InlineStack>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
          {(['A', 'B'] as const).map((key) => {
            const v = g.variants[key];
            if (!v) return null;
            const isWinner = winner === key;
            return (
              <Box
                key={key}
                padding="300"
                borderRadius="200"
                background={isWinner ? 'bg-fill-success-secondary' : 'bg-surface'}
                borderColor="border"
                borderWidth="025"
              >
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center">
                    <Badge tone={isWinner ? 'success' : 'info'}>{`Variant ${key}`}</Badge>
                    {isWinner ? <Text as="span" variant="bodySm">⭐</Text> : null}
                  </InlineStack>
                  <Text as="p" variant="bodySm" fontWeight="medium">{v.title}</Text>
                  <Divider />
                  <InlineStack gap="300">
                    <Text as="span" variant="bodySm">
                      Sent <strong>{v.sent}</strong>
                    </Text>
                    <Text as="span" variant="bodySm">
                      Open <strong>{v.opened}</strong>
                      {v.sent ? ` (${Math.round((v.opened / v.sent) * 100)}%)` : ''}
                    </Text>
                    <Text as="span" variant="bodySm">
                      Click <strong>{v.clicked}</strong>
                    </Text>
                  </InlineStack>
                </BlockStack>
              </Box>
            );
          })}
        </InlineGrid>
      </BlockStack>
    </Box>
  );
}
